import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTaskMasterEntryScreen } from './hooks/useTaskMasterEntryScreen'
import { appToast } from '../../../components/AppToast'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

const PRIORITIES = ['HIGH', 'MEDIUM', 'LOW']

function priorityMeta(p) {
  if (p === 'HIGH') return { bg: 'bg-error-container', text: 'text-error', border: 'border-error/30', dot: 'bg-error' }
  if (p === 'MEDIUM') return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-400/30', dot: 'bg-amber-500' }
  return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-400/30', dot: 'bg-emerald-500' }
}

function slaFill(hours) {
  return Math.min(100, ((hours || 0) / 120) * 100)
}

function slaColor(hours) {
  if ((hours || 0) <= 12) return 'bg-emerald-500'
  if ((hours || 0) <= 48) return 'bg-amber-500'
  return 'bg-error'
}

export default function TaskMasterEntryScreen() {
  const { state, actions } = useTaskMasterEntryScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const meta = priorityMeta(state.form.default_priority || 'MEDIUM')
  const nameInitials = String(state.form.task_type_name || 'NB')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || 'NB'

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-8 pb-10">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-secondary-container px-3 py-1">
            <span className="material-symbols-outlined text-sm text-primary">assignment</span>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-on-secondary-container">
              Blueprint Registry
            </span>
          </div>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Blueprint' : 'New Task Blueprint'}
          </h1>
          <p className="mt-2 text-sm text-on-surface-variant">
            Define SLA windows and effort estimates that tasks inherit when created from this template.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {!state.pendingApproval && (
            <Link
              to="/masters/task-master/list"
              className={`rounded-lg bg-surface-container-high px-6 py-2.5 text-sm font-bold text-on-surface-variant transition hover:bg-surface-container-highest ${
                state.isSaving ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              Discard
            </Link>
          )}
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading || Boolean(state.pendingApproval)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-2.5 text-sm font-bold text-on-primary shadow-md shadow-primary/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">
              {state.pendingApproval ? 'schedule' : 'send'}
            </span>
            {state.pendingApproval ? 'Submitted for Approval' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </header>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading blueprint...
        </div>
      ) : (
        <>
          {/* ── 2-col layout: form left, live card right ─── */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
            {/* Left: all form fields */}
            <div className="space-y-6">
              {/* Blueprint Name */}
              <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-7">
                <div className="mb-5 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">label</span>
                  <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">Template Identity</h2>
                </div>

                <div className="mb-4">
                  <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Task Type Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={state.form.task_type_name}
                    onChange={(e) => actions.patchForm('task_type_name', e.target.value)}
                    placeholder="e.g., Client Incident Handling"
                    className="mt-2 w-full rounded-xl border-none bg-surface-container-low px-5 py-4 text-xl font-bold text-on-surface outline-none placeholder:text-on-surface-variant/50 transition focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                {/* Skill Tags */}
                <label className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Skill Tags <span className="text-on-surface-variant/50">(comma separated)</span>
                  </span>
                  <div className="relative">
                    <span className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-outline">
                      sell
                    </span>
                    <input
                      type="text"
                      value={state.form.skill_tags}
                      onChange={(e) => actions.patchForm('skill_tags', e.target.value)}
                      placeholder="e.g., support, database, billing"
                      className="w-full rounded-xl border-none bg-surface-container-low py-3 pl-11 pr-4 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/50"
                    />
                  </div>
                  {state.form.skill_tags && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {state.form.skill_tags.split(',').map((tag, idx) =>
                        tag.trim() ? (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 rounded-full bg-secondary-container px-3 py-1 text-[11px] font-bold text-on-secondary-container"
                          >
                            <span className="material-symbols-outlined text-xs">sell</span>
                            {tag.trim()}
                          </span>
                        ) : null,
                      )}
                    </div>
                  )}
                </label>
              </section>

              {/* SLA & Effort */}
              <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-7">
                <div className="mb-5 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">timer</span>
                  <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">SLA & Effort</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="grid gap-2">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      Default Effort Estimate <span className="text-error">*</span>
                    </span>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="grid gap-1">
                        <span className="text-[10px] font-semibold text-outline uppercase tracking-wider">Hours</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={state.form.effort_hours}
                          onChange={(e) => actions.patchForm('effort_hours', e.target.value)}
                          placeholder="e.g. 4"
                          className="w-full rounded-xl border-none bg-surface-container-low py-3 px-4 text-sm text-on-surface outline-none"
                        />
                      </label>
                      <label className="grid gap-1">
                        <span className="text-[10px] font-semibold text-outline uppercase tracking-wider">Minutes</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          step="1"
                          value={state.form.effort_minutes}
                          onChange={(e) => actions.patchForm('effort_minutes', e.target.value)}
                          placeholder="e.g. 30"
                          className="w-full rounded-xl border-none bg-surface-container-low py-3 px-4 text-sm text-on-surface outline-none"
                        />
                      </label>
                    </div>
                  </div>

                  <label className="grid gap-2">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      SLA Window <span className="text-error">*</span>
                    </span>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={state.form.sla_hours}
                        onChange={(e) => actions.patchForm('sla_hours', e.target.value)}
                        placeholder="8"
                        className="w-full rounded-xl border-none bg-surface-container-low py-3 pl-4 pr-14 text-sm text-on-surface outline-none"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-on-surface-variant">hrs</span>
                    </div>
                  </label>
                </div>

                {/* SLA visual bar */}
                {Number(state.form.sla_hours) > 0 && (
                  <div className="mt-5 rounded-xl bg-surface-container-low p-4">
                    <div className="mb-2 flex items-center justify-between text-xs font-bold text-on-surface-variant">
                      <span>SLA Severity</span>
                      <span className={slaColor(Number(state.form.sla_hours)).replace('bg-', 'text-')}>
                        {Number(state.form.sla_hours) <= 12
                          ? 'Low — within working day'
                          : Number(state.form.sla_hours) <= 48
                          ? 'Medium — within 2 days'
                          : 'High — multi-day window'}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-container-high">
                      <div
                        className={`h-full rounded-full transition-all ${slaColor(Number(state.form.sla_hours))}`}
                        style={{ width: `${slaFill(Number(state.form.sla_hours))}%` }}
                      />
                    </div>
                  </div>
                )}
              </section>

              {/* Priority & Status */}
              <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-7">
                <div className="mb-5 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">tune</span>
                  <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">Configuration</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Priority selector */}
                  <div className="grid gap-3">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Default Priority</span>
                    <div className="flex gap-2">
                      {PRIORITIES.map((p) => {
                        const m = priorityMeta(p)
                        const isActive = state.form.default_priority === p
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={() => actions.patchForm('default_priority', p)}
                            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-black uppercase tracking-wider transition-all ${
                              isActive
                                ? `${m.bg} ${m.text} ${m.border} shadow-sm scale-[1.03]`
                                : 'border-outline-variant/20 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                            }`}
                          >
                            {isActive && <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />}
                            {p}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Status toggle */}
                  <div className="grid gap-3">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Operational Status</span>
                    <div className="flex gap-2 rounded-xl bg-surface-container-low p-1">
                      <button
                        type="button"
                        onClick={() => actions.setOperationalStatus('active')}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold transition-all ${
                          state.operationalStatus === 'active'
                            ? 'bg-surface-container-lowest text-primary shadow-sm'
                            : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        Active
                      </button>
                      <button
                        type="button"
                        onClick={() => actions.setOperationalStatus('inactive')}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold transition-all ${
                          state.operationalStatus === 'inactive'
                            ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                            : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        <span className="h-2 w-2 rounded-full bg-outline" />
                        Inactive
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Right: Live blueprint preview */}
            <aside className="space-y-5">
              {/* Live Blueprint Card */}
              <div className="sticky top-6 space-y-5">
                <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary-container/20 to-surface-container-lowest p-5">
                  <p className="mb-4 text-[10px] font-black uppercase tracking-[0.18em] text-primary">Live Preview</p>

                  <div className={`rounded-2xl border ${meta.border} ${meta.bg}/30 p-5`}>
                    {/* Blueprint header */}
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-container text-sm font-black text-primary">
                          {nameInitials}
                        </div>
                        <div>
                          <h4 className="text-sm font-extrabold text-on-surface line-clamp-1">
                            {state.form.task_type_name || 'Blueprint Name'}
                          </h4>
                          <p className="text-[9px] font-mono text-on-surface-variant uppercase tracking-wider">
                            {state.form.id ? `TCK-${state.form.id}` : 'TCK-DRAFT'}
                          </p>
                        </div>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${meta.bg} ${meta.text} ${meta.border}`}>
                        {state.form.default_priority || 'MEDIUM'}
                      </span>
                    </div>

                    {/* SLA bar */}
                    <div className="mb-3">
                      <div className="mb-1.5 flex justify-between text-[10px] font-bold text-on-surface-variant">
                        <span>SLA Timeline</span>
                        <span>{state.form.sla_hours || 0} hrs</span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-high">
                        <div
                          className={`h-full rounded-full ${slaColor(Number(state.form.sla_hours))}`}
                          style={{ width: `${slaFill(Number(state.form.sla_hours))}%` }}
                        />
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center justify-between rounded-lg bg-surface-container-lowest/70 px-3 py-2 text-[10px]">
                      <div>
                        <p className="font-bold text-on-surface">Effort</p>
                        <p className="text-on-surface-variant">{Number(state.form.default_estimated_hours || 0).toFixed(1)} hrs</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-on-surface">Status</p>
                        <span className={`inline-flex items-center gap-1 font-bold ${state.operationalStatus === 'active' ? 'text-emerald-600' : 'text-on-surface-variant'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${state.operationalStatus === 'active' ? 'bg-emerald-600' : 'bg-outline'}`} />
                          {state.operationalStatus === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {/* Skill tags */}
                    {state.form.skill_tags && (
                      <div className="mt-3">
                        <p className="mb-1 text-[9px] font-black uppercase tracking-wider text-on-surface-variant">Skills</p>
                        <div className="flex flex-wrap gap-1">
                          {state.form.skill_tags.split(',').map((tag, i) =>
                            tag.trim() ? (
                              <span key={i} className="rounded bg-surface-container-high px-1.5 py-0.5 text-[9px] font-medium text-on-surface-variant">
                                {tag.trim()}
                              </span>
                            ) : null,
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Completion progress chip */}
                <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Completion</span>
                    <span className={`text-lg font-black ${state.completionPercent >= 80 ? 'text-emerald-600' : state.completionPercent >= 50 ? 'text-amber-600' : 'text-error'}`}>
                      {state.completionPercent}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-variant">
                    <div
                      className={`h-full rounded-full transition-all ${state.completionPercent >= 80 ? 'bg-emerald-500' : state.completionPercent >= 50 ? 'bg-amber-500' : 'bg-error'}`}
                      style={{ width: `${Math.max(4, state.completionPercent)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-[10px] text-outline">Based on required and optional fields.</p>
                </div>

                {/* Helpful info */}
                <div className="rounded-2xl bg-surface-container-low p-5 space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary">About Blueprints</h3>
                  <div className="space-y-2.5 text-xs text-on-surface-variant">
                    <p className="flex items-start gap-2">
                      <span className="material-symbols-outlined mt-0.5 text-sm text-primary">info</span>
                      Task templates must have unique names across the organization.
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="material-symbols-outlined mt-0.5 text-sm text-primary">warning</span>
                      SLA violations trigger manager escalation rules automatically.
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="material-symbols-outlined mt-0.5 text-sm text-primary">bolt</span>
                      Tasks created from this blueprint inherit all default values here.
                    </p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  )
}
